import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import type * as Leaflet from 'leaflet';

import { environment } from '@env/environment';

export interface LocationCoordinates {
  latitud: string;
  longitud: string;
}

const DEFAULT_CENTER: Leaflet.LatLngTuple = [-23.4425, -58.4438];
const DEFAULT_ZOOM = 6;
const SELECTED_LOCATION_ZOOM = 15;

@Component({
  selector: 'app-location-picker',
  templateUrl: './location-picker.component.html',
  styleUrl: './location-picker.component.scss',
})
export class LocationPickerComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() latitud: string | number | null | undefined;
  @Input() longitud: string | number | null | undefined;
  @Input() disabled = false;

  @Output() coordinatesChange = new EventEmitter<LocationCoordinates>();

  @ViewChild('mapContainer', { static: true })
  private mapContainer!: ElementRef<HTMLDivElement>;

  private readonly ngZone = inject(NgZone);
  private leaflet?: typeof import('leaflet/dist/leaflet-src.esm.js');
  private map?: Leaflet.Map;
  private marker?: Leaflet.Marker;
  private initializationPromise?: Promise<void>;
  private destroyed = false;

  ngAfterViewInit(): void {
    this.initializationPromise = this.initializeMap();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled']) {
      this.syncMarkerState();
    }

    if (changes['latitud'] || changes['longitud']) {
      this.syncCoordinatesFromInputs();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.map?.remove();
    this.map = undefined;
    this.marker = undefined;
    this.leaflet = undefined;
  }

  private async initializeMap(): Promise<void> {
    const leaflet = await import('leaflet/dist/leaflet-src.esm.js');
    if (this.destroyed) {
      return;
    }

    this.leaflet = leaflet;
    const coordinates = this.getValidCoordinates();

    this.map = leaflet.map(this.mapContainer.nativeElement, {
      center: coordinates ?? DEFAULT_CENTER,
      zoom: coordinates ? SELECTED_LOCATION_ZOOM : DEFAULT_ZOOM,
    });

    leaflet.tileLayer(environment.mapTileUrl, {
      attribution: environment.mapAttribution,
      maxZoom: 19,
    }).addTo(this.map);

    this.map.on('click', (event: Leaflet.LeafletMouseEvent) => {
      if (!this.disabled) {
        this.selectCoordinates(event.latlng);
      }
    });

    if (coordinates) {
      this.setMarker(coordinates);
    }

    window.setTimeout(() => this.map?.invalidateSize(), 0);
  }

  private syncCoordinatesFromInputs(): void {
    if (!this.map) {
      return;
    }

    const coordinates = this.getValidCoordinates();
    if (!coordinates) {
      this.marker?.remove();
      this.marker = undefined;
      return;
    }

    const hadMarker = Boolean(this.marker);
    this.setMarker(coordinates);
    if (hadMarker) {
      this.map.panTo(coordinates);
    } else {
      this.map.setView(coordinates, SELECTED_LOCATION_ZOOM);
    }
  }

  private selectCoordinates(coordinates: Leaflet.LatLng): void {
    this.setMarker(coordinates);
    this.ngZone.run(() => {
      this.coordinatesChange.emit({
        latitud: coordinates.lat.toFixed(6),
        longitud: coordinates.lng.toFixed(6),
      });
    });
  }

  private setMarker(coordinates: Leaflet.LatLng): void {
    if (this.marker) {
      this.marker.setLatLng(coordinates);
      return;
    }

    this.marker = this.leaflet!.marker(coordinates, {
      autoPan: true,
      draggable: !this.disabled,
      icon: this.createMarkerIcon(),
    }).addTo(this.map!);

    this.marker.on('dragend', () => {
      if (!this.disabled && this.marker) {
        this.selectCoordinates(this.marker.getLatLng());
      }
    });
  }

  private createMarkerIcon(): Leaflet.DivIcon {
    return this.leaflet!.divIcon({
      className: 'location-map-marker',
      html: '<span class="location-map-marker__pin" aria-hidden="true"></span>',
      iconAnchor: [14, 28],
      iconSize: [28, 28],
    });
  }

  private syncMarkerState(): void {
    if (!this.marker) {
      return;
    }

    if (this.disabled) {
      this.marker.dragging?.disable();
    } else {
      this.marker.dragging?.enable();
    }
  }

  private getValidCoordinates(): Leaflet.LatLng | null {
    const latitud = this.parseCoordinate(this.latitud, -90, 90);
    const longitud = this.parseCoordinate(this.longitud, -180, 180);

    return latitud === null || longitud === null
      ? null
      : this.leaflet!.latLng(latitud, longitud);
  }

  private parseCoordinate(
    value: string | number | null | undefined,
    min: number,
    max: number,
  ): number | null {
    if (
      value === null ||
      value === undefined ||
      value === '' ||
      (typeof value === 'string' && value.trim() === '')
    ) {
      return null;
    }

    const coordinate = Number(value);
    return Number.isFinite(coordinate) && coordinate >= min && coordinate <= max
      ? coordinate
      : null;
  }
}
